import {
  CloseSmIcon,
  DotSpinner,
  IconButton,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  SearchMdIcon,
  Switch,
} from '@crono-one/design-system';
import cn from 'clsx';
import { useLoadMoreSentinel } from 'crono-fe-common/components/InfiniteCombobox/shared';
import SequenceStartDatePicker from 'crono-fe-common/components/SequencePicker/SequenceStartDatePicker';
import { StrategySearchEmptyState } from 'crono-fe-common/components/StrategySearchEmptyState';
import { CSSProperties, ReactNode, useEffect, useId, useRef } from 'react';

export interface SequencePickerPanelProps<T> {
  search: string;
  onSearchChange: (search: string) => void;
  //Focused on mount so the user can type straight into the popup
  autoFocusSearch?: boolean;
  /**
   * For a host that already owns a search field of its own — the filters drawer
   * anchors its popup to one. The panel then drops its own field and moves the
   * my-sequences switch below the filters row, where the search row used to be.
   */
  hideSearch?: boolean;

  myOnly: boolean;
  onMyOnlyChange: (myOnly: boolean) => void;

  //Filled by the host, which owns the fetching: React Query in the app, IPC in
  //the extension. Anything omitted simply leaves its slot out of the row
  listsFilter?: ReactNode;
  tagsFilter?: ReactNode;
  sortPicker?: ReactNode;
  //Sits at the end of the search row — the extension puts its close button here
  headerEnd?: ReactNode;

  items: T[];
  getItemKey: (item: T) => string | number;
  renderItem: (item: T) => ReactNode;
  isLoading?: boolean;
  //Infinite scroll: a host that pages its results passes fetchNextPage, and the
  //next page is requested once the end of the list scrolls into view
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;

  //Off for the pickers that only choose a sequence without enrolling anyone —
  //the filters and the conditional step's branch target — where a date means nothing
  enableStartDate?: boolean;
  startDate?: Date;
  onStartDateChange?: (date: Date | undefined) => void;

  footerStart?: ReactNode;
  footerEnd?: ReactNode;

  /**
   * `popover` sizes the panel itself, for a picker that opens inside a tooltip.
   * `modal` fills whatever the host centres it in.
   */
  variant?: 'popover' | 'modal';
  className?: string;
  //Lets a host that frames the panel itself override the sizing above
  style?: CSSProperties;
}

interface MyOnlyToggleProps {
  myOnly: boolean;
  onMyOnlyChange: (myOnly: boolean) => void;
  headerEnd?: ReactNode;
}

const MyOnlyToggle = ({
  myOnly,
  onMyOnlyChange,
  headerEnd,
}: MyOnlyToggleProps) => {
  const switchId = useId();

  return (
    <div className="flex shrink-0 items-center gap-2">
      <label
        htmlFor={switchId}
        className="whitespace-nowrap text-xs font-medium text-gray-700"
      >
        My sequences
      </label>
      <Switch id={switchId} checked={myOnly} onCheckedChange={onMyOnlyChange} />
      {headerEnd}
    </div>
  );
};

/**
 * The sequence picker, shared by the web app and the LinkedIn extension so a
 * design change lands in both at once. It renders the chrome, the header, the
 * list and the footer, and is fully controlled — every filter's data and state
 * belongs to the host.
 */
const SequencePickerPanel = <T,>({
  search,
  onSearchChange,
  autoFocusSearch = true,
  hideSearch = false,
  myOnly,
  onMyOnlyChange,
  listsFilter,
  tagsFilter,
  sortPicker,
  headerEnd,
  items,
  getItemKey,
  renderItem,
  isLoading = false,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  enableStartDate = false,
  startDate,
  onStartDateChange,
  footerStart,
  footerEnd,
  variant = 'popover',
  className,
  style,
}: SequencePickerPanelProps<T>) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocusSearch) {
      searchInputRef.current?.focus();
    }
  }, [autoFocusSearch]);

  const setLoadMoreSentinel = useLoadMoreSentinel({
    fetchNextPage: fetchNextPage ?? (() => undefined),
    hasNextPage: !!fetchNextPage && hasNextPage,
    isFetchingNextPage,
  });

  const hasFooter = enableStartDate || !!footerStart || !!footerEnd;

  return (
    <div
      className={cn(
        'relative flex flex-col overflow-hidden text-sm',
        variant === 'popover' ? 'h-full max-h-110 min-h-110 w-99' : 'size-full',
        className,
      )}
      style={style}
    >
      <div className="flex flex-col gap-2 border-b border-neutral-300 p-2">
        {!hideSearch && (
          <div className="flex items-center justify-between gap-2 py-1">
            <InputGroup className="min-w-0 flex-1" variant="text">
              <InputGroupAddon>
                <SearchMdIcon />
              </InputGroupAddon>
              <InputGroupInput
                ref={searchInputRef}
                placeholder="Search..."
                autoComplete="off"
                value={search}
                onChange={(ev) => onSearchChange(ev.target.value)}
              />
              {!!search && (
                <InputGroupAddon align="inline-end">
                  <IconButton
                    variant="text"
                    tone="gray"
                    size="xs"
                    label="Clear search"
                    icon={CloseSmIcon}
                    onClick={() => onSearchChange('')}
                  />
                </InputGroupAddon>
              )}
            </InputGroup>

            <MyOnlyToggle
              myOnly={myOnly}
              onMyOnlyChange={onMyOnlyChange}
              headerEnd={headerEnd}
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          {!!listsFilter && (
            <div className="w-27.5 shrink-0">{listsFilter}</div>
          )}
          {tagsFilter}
          {!!sortPicker && (
            <div className="flex min-w-0 flex-1 justify-end">{sortPicker}</div>
          )}
        </div>

        {hideSearch && (
          <MyOnlyToggle
            myOnly={myOnly}
            onMyOnlyChange={onMyOnlyChange}
            headerEnd={headerEnd}
          />
        )}
      </div>

      {isLoading ? (
        <div className="flex w-full justify-center py-2">
          <DotSpinner />
        </div>
      ) : (
        //Scrolls vertically only: a long sequence name truncates rather than
        //widening the list, which the extension's narrow popup would otherwise
        //turn into a horizontal scrollbar
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-1">
          {items.length > 0 ? (
            <>
              {items.map((item, index) => (
                <div key={getItemKey(item)}>
                  {renderItem(item)}
                  {index < items.length - 1 && (
                    <div className="my-1 border-b border-gray-200" />
                  )}
                </div>
              ))}
              {!!fetchNextPage && hasNextPage && (
                <div ref={setLoadMoreSentinel} className="h-px" />
              )}
              {isFetchingNextPage && (
                <div
                  role="status"
                  aria-label="Loading more sequences"
                  className="flex w-full justify-center py-2"
                >
                  <DotSpinner />
                </div>
              )}
            </>
          ) : (
            <StrategySearchEmptyState
              style={{ paddingBottom: '48px' }}
              size="small"
            />
          )}
        </div>
      )}

      {hasFooter && (
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-neutral-300 p-2">
          {enableStartDate && (
            //Allowed to shrink and truncate: the extension's popup can be
            //narrow, and the confirm button must never be pushed out of it
            <div className="min-w-0 flex-1">
              <SequenceStartDatePicker
                value={startDate}
                onChange={onStartDateChange ?? (() => undefined)}
              />
            </div>
          )}
          {footerStart}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {footerEnd}
          </div>
        </div>
      )}
    </div>
  );
};

export default SequencePickerPanel;
